import React, { useEffect, useMemo, useState } from 'react';
import { fetchStudents } from '../../../services/studentService';

// Portal Phase C — student link picker (C3). A real picker bound to the
// `students` collection: loads the roster on open, search-filters client-side,
// and returns { id, name } to the caller. If the read is denied / empty, it
// degrades to a calm empty state (the binding is real but data may not be
// reachable in every context — acceptable for C3).

function studentName(s) {
	return (
		s.fullName ||
		[s.firstName, s.lastName].filter(Boolean).join(' ') ||
		s.name ||
		'Unnamed student'
	);
}

function StudentPicker({ onSelect, onClose }) {
	const [students, setStudents] = useState(null); // null = loading
	const [q, setQ] = useState('');

	useEffect(() => {
		let alive = true;
		fetchStudents()
			.then((rows) => {
				if (alive) setStudents(rows);
			})
			.catch(() => {
				if (alive) setStudents([]);
			});
		return () => {
			alive = false;
		};
	}, []);

	const filtered = useMemo(() => {
		if (!students) return [];
		const needle = q.trim().toLowerCase();
		const list = students
			.map((s) => ({ id: s.id, name: studentName(s) }))
			.sort((a, b) => a.name.localeCompare(b.name));
		if (!needle) return list.slice(0, 60);
		return list.filter((s) => s.name.toLowerCase().includes(needle)).slice(0, 60);
	}, [students, q]);

	return (
		<div className="wt-overlay" onClick={onClose}>
			<div
				className="wt-sheet"
				onClick={(e) => e.stopPropagation()}
				style={{ '--row-accent': 'var(--cyv-teal-600)' }}>
				<div className="wt-sheet__bar">
					<span className="wt-sheet__eyebrow">Link a student</span>
					<button
						type="button"
						className="wt-iconbtn"
						aria-label="Close"
						onClick={onClose}>
						<i className="fa-sharp fa-solid fa-xmark" />
					</button>
				</div>
				<div className="wt-sheet__body">
					<div className="wt-search">
						<i className="fa-sharp fa-solid fa-magnifying-glass" />
						{/* eslint-disable-next-line jsx-a11y/no-autofocus */}
						<input
							autoFocus
							value={q}
							placeholder="Search students…"
							onChange={(e) => setQ(e.target.value)}
						/>
					</div>
					{students === null ? (
						<div className="wt-skel" />
					) : filtered.length === 0 ? (
						<div className="wt-empty">
							<span className="wt-empty__bubble">
								<i className="fa-sharp-duotone fa-solid fa-graduation-cap" />
							</span>
							<div className="wt-empty__title">No students found</div>
							<div className="wt-empty__sub">
								The roster may not be loaded in this view.
							</div>
						</div>
					) : (
						<div className="wt-list">
							{filtered.map((s) => (
								<button
									key={s.id}
									type="button"
									className="wt-linkrow"
									onClick={() => onSelect(s)}>
									<i className="fa-sharp-duotone fa-solid fa-user" />
									{s.name}
								</button>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default StudentPicker;
